import SideDrawer from './side-drawer.js';
import { debounce } from '../../utils/utils.js';
import { initLazyImages } from '../../utils/lazy-images.js';

/**
 * Configuration object for CartDrawer selectors and constants
 */
const CART_CONFIG = {
    SECTIONS: ['cart-drawer', 'cart-icon-bubble'],
    SELECTORS: {
        LINE_ITEM: (line) => `#line-item-${line}`,
        REMOVE_BUTTON: '.js-remove-item',
        CART_ICON_BUBBLE: 'cart-icon-bubble'
    },
    EVENTS: {
        CART_ADD: 'on:cart:add'
    },
    CLASSES: {
        LOADING: 'is-loading'
    }
};

class CartDrawer extends SideDrawer {
    static sections = CART_CONFIG.SECTIONS;

    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();
        this.setupCartListeners();
    }

    /**
     * Setup cart-specific event listeners
     */
    setupCartListeners() {
        document.addEventListener(CART_CONFIG.EVENTS.CART_ADD, this.handleCartAddEvent.bind(this));
        this.addEventListener('change', debounce(this.handleChange.bind(this)));

        // Use delegateClick for remove button handling
        const cleanup = this.delegateClick(CART_CONFIG.SELECTORS.REMOVE_BUTTON, (match, evt) => {
            evt.preventDefault();
            this.updateQuantity(match.dataset.index, 0);
        });

        // Store cleanup function for later removal
        this._delegatedCleanupFunctions.push(cleanup);
    }

    /**
     * Handles 'change' events on the cart drawer element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
        this.updateQuantity(evt.target.dataset.index, evt.target.value);
    }

    shouldOpenOnSectionSelect() {
        return true;
    }

    async updateQuantity(line, quantity) {
        try {
            const cartItemEl = this.querySelector(CART_CONFIG.SELECTORS.LINE_ITEM(line));
            cartItemEl.classList.add(CART_CONFIG.CLASSES.LOADING);

            const response = await fetch(theme.routes.cartChange, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    line,
                    quantity,
                    sections: CartDrawer.sections,
                    sections_url: window.location.pathname
                })
            });

            const data = await response.json();

            if (!response.ok) throw new Error(data.errors || response.status);

            this.renderContents(data.sections);
        } catch (error) {
            //display error for line
            console.error(error);
        }
    }

    handleCartAddEvent(e) {
        if (e.detail?.sections && e.detail?.sections['cart-drawer']) {
            this.renderContents(e.detail.sections);
        } else {
            this.refresh();
        }
    }

    async refresh() {
        const response = await fetch('?sections=cart-drawer').then((res) => res.json());
        this.renderContents(response, false);
    }

    renderContents(sections, openCart = true) {
        if (sections['cart-drawer']) {
            this.innerHTML = CartDrawer.getSafeElementHTML(sections['cart-drawer']);

            if (openCart && !this.isOpen) {
                setTimeout(() => this.open());
            }
        }

        if (sections['cart-icon-bubble']) {
            CartDrawer.updateCartIcon(sections);
        }

        initLazyImages();
    }

    /**
     * Gets the safe innerHTML of the updated cart drawer.
     * @param {string} html - Section HTML.
     * @returns {string}
     */
    static getSafeElementHTML(html) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = html;

        return tmpl.content.querySelector('cart-drawer').innerHTML;
    }

    static updateCartIcon(sections) {
        const cartIconBubble = document.getElementById(CART_CONFIG.SELECTORS.CART_ICON_BUBBLE);
        if (cartIconBubble) {
            cartIconBubble.innerHTML = sections[CART_CONFIG.SECTIONS[1]];
        }
    }
}

customElements.define('cart-drawer', CartDrawer);
